import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Calendar, Info } from 'lucide-react';
import { formatDateToISO, generateTimeOptions, isPracticeClosed } from '../../utils/helpers';
import { RootState } from '../../redux/store';
import { TIME_SLOT_INCREMENT_MINUTES } from '../../utils/constants';
import { TimeSelect } from '../ui/TimeSelect';

export interface PreferredSlot {
  date: Date | undefined;
  startTime: string;
  endTime: string;
  isAnyTime: boolean;
}

interface PreferredSlotCardProps {
  slot: PreferredSlot;
  onSlotChange: (slot: PreferredSlot) => void;
  priority: number;
  label: string;
  badgeType: 'primary' | 'outline';
  statusLabel: string;
  statusBadgeStyles: string;
  disabled?: boolean;
}

export const PreferredSlotCard: React.FC<PreferredSlotCardProps> = ({
  slot,
  onSlotChange,
  priority,
  label,
  badgeType,
  statusLabel,
  statusBadgeStyles,
  disabled = false
}) => {
  const { practice } = useSelector((state: RootState) => state.auth);

  const dayName = useMemo(() =>
    slot.date?.toLocaleDateString('en-US', { weekday: 'long' }),
    [slot.date]
  );

  const timeOptions = useMemo(() =>
    generateTimeOptions(
      practice?.working_hours,
      dayName,
      TIME_SLOT_INCREMENT_MINUTES,
      slot.date
    ),
    [practice?.working_hours, dayName, slot.date]
  );

  const startTimeOptions = useMemo(() =>
    timeOptions.slice(0, -1),
    [timeOptions]
  );

  const endTimeOptions = useMemo(() =>
    timeOptions.filter(o => o.value > slot.startTime),
    [timeOptions, slot.startTime]
  );

  const isPracticeClosedDay = useMemo(() =>
    isPracticeClosed(practice?.working_hours, slot.date),
    [practice?.working_hours, slot.date]
  );

  return (
    <div className={`card bg-base-100 border-2 shadow-sm transition-all ${disabled
      ? 'border-base-200 opacity-50 pointer-events-none'
      : badgeType === 'primary' ? 'border-base-300' : 'border-base-600'}`}>
      <div className="card-body p-4">
        <div className="flex items-center gap-2">
          <div className={`badge badge-${badgeType} badge-lg font-bold ${badgeType === 'primary' ? 'text-white' : 'border-base-300'}`}>{priority}</div>
          <h2 className="card-title text-base">{label}</h2>
          <div className={`badge ${statusBadgeStyles} text-xs p-2 ml-auto`}>{statusLabel}</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="form-control">
            <label className="label pb-1 flex justify-between px-0">
              <span className="label-text text-sm font-medium">Select Date</span>
              {isPracticeClosedDay && (
                <span className="text-red-600 text-[10px] sm:text-xs font-semibold bg-red-50 px-2 py-0.5 rounded-full border border-red-100 flex items-center gap-1">
                  <Info size={12} />
                  Practice closed
                </span>
              )}
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-base-content/50 pointer-events-none" />
              <input
                type="date"
                className={`input input-bordered w-full pl-10 h-9 ${isPracticeClosedDay ? 'border-red-200 focus:border-red-300 focus:ring-red-200' : ''}`}
                value={slot.date ? formatDateToISO(slot.date) : ''}
                min={formatDateToISO(new Date())}
                disabled={disabled}
                onChange={(e) => {
                  const val = e.target.value;
                  onSlotChange({
                    ...slot,
                    date: val ? new Date(val + 'T00:00:00') : undefined
                  });
                }}
              />
            </div>
          </div>

          <div className={`form-control ${isPracticeClosedDay ? 'opacity-60' : ''}`}>
            <label className="label pb-1">
              <span className="label-text text-sm font-medium">Time Preference</span>
              <label className="label cursor-pointer gap-2 p-0">
                <input
                  type="checkbox"
                  className="checkbox checkbox-xs checkbox-neutral"
                  checked={slot.isAnyTime}
                  disabled={disabled || isPracticeClosedDay}
                  onChange={(e) => onSlotChange({
                    ...slot, isAnyTime: e.target.checked,
                    startTime: e.target.checked ? '' : slot.startTime,
                    endTime: e.target.checked ? '' : slot.endTime
                  })}
                />
                <span className="label-text text-xs">Any Time</span>
              </label>
            </label>

            {slot.isAnyTime ? (
              <div className={`input input-bordered h-9 flex items-center bg-base-200 text-base-content/60 ${isPracticeClosedDay ? 'border-base-200' : ''}`}>
                Available all day
              </div>
            ) : (
              <div className="flex gap-2">
                <TimeSelect
                  value={slot.startTime}
                  options={startTimeOptions}
                  onChange={(v) => onSlotChange({ ...slot, startTime: v, endTime: slot.endTime && slot.endTime <= v ? '' : slot.endTime })}
                  placeholder="Start"
                  disabled={disabled || isPracticeClosedDay || !dayName || timeOptions.length === 0}
                  showIcon={false}
                />
                <TimeSelect
                  value={slot.endTime}
                  options={endTimeOptions}
                  onChange={(v) => onSlotChange({ ...slot, endTime: v })}
                  placeholder="End"
                  disabled={disabled || isPracticeClosedDay || !slot.startTime}
                  showIcon={false}
                />
              </div>
            )}
          </div>
        </div>
        {priority === 2 && (
          <div className="flex items-center mt-2 p-2 bg-gray-50 text-gray-500 text-xs sm:text-sm rounded-md flex items-start gap-2">
            <Info size={15} className='flex-shrink-0 hidden sm:block' />
            <span><span className='font-semibold block sm:hidden'>Note:</span>Incomplete time selection (e.g. Date only or Date + Start Time) will be considered as 'Any Time'.</span>
          </div>
        )}
      </div>
    </div>
  );
};